// VNEC v9 — lib/pdf-export.js
// Export PYCTT (Phiếu YCTT) to A4 landscape PDF via window.print()
// Layout based on v8 PDF + v9 enhancements:
//   - Project name in header
//   - Loại CP / IFRS column
//   - Horizontal disbursement tracker
//   - Status watermark
//   - SHA-256 hash for tamper detection
//   - QR code placeholder

import { supabase } from './supabase.js';

// ── Helpers ──────────────────────────────────────────────────────────
const F = (n) => Math.round(Number(n) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const E = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function fmtDateVN(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('vi-VN');
}

// Vietnamese number-to-words (simplified for round VND amounts)
function soThanhChu(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return 'Không đồng';
  const dv = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  function readBlock(num) {
    const t = Math.floor(num / 100);
    const c = Math.floor((num % 100) / 10);
    const d = num % 10;
    let s = '';
    if (t > 0) s += dv[t] + ' trăm ';
    if (c > 1) { s += dv[c] + ' mươi '; if (d === 1) s += 'mốt '; else if (d === 5) s += 'lăm '; else if (d > 0) s += dv[d] + ' '; }
    else if (c === 1) { s += 'mười '; if (d === 5) s += 'lăm '; else if (d > 0) s += dv[d] + ' '; }
    else if (c === 0 && d > 0) { if (t > 0) s += 'lẻ '; s += dv[d] + ' '; }
    return s.trim();
  }
  const ty = Math.floor(n / 1e9);
  const trieu = Math.floor((n % 1e9) / 1e6);
  const nghin = Math.floor((n % 1e6) / 1e3);
  const dong = n % 1000;
  let s = '';
  if (ty > 0) s += readBlock(ty) + ' tỷ ';
  if (trieu > 0) s += readBlock(trieu) + ' triệu ';
  if (nghin > 0) s += readBlock(nghin) + ' nghìn ';
  if (dong > 0) s += readBlock(dong) + ' ';
  s = s.trim() + ' đồng';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// SHA-256 hash → first 8 hex chars (Web Crypto API native)
async function shortHash(text) {
  try {
    const buf = new TextEncoder().encode(text);
    const hash = await crypto.subtle.digest('SHA-256', buf);
    const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 8);
  } catch { return '00000000'; }
}

// Watermark text + color by trang_thai
function watermarkInfo(state) {
  switch (state) {
    case 'draft':       return { text: 'DỰ THẢO',       color: '#888888' };
    case 'pending_l1':  return { text: 'CHỜ DUYỆT L1', color: '#FFCC00' };
    case 'pending_l2':  return { text: 'CHỜ DUYỆT L2', color: '#FFCC00' };
    case 'rejected':    return { text: 'TỪ CHỐI',       color: '#c4000a' };
    case 'cancelled':   return { text: 'ĐÃ HỦY',       color: '#1a1a1a' };
    default:            return null; // approved → no watermark
  }
}

// IFRS bucket badge (color + label)
function ifrsBadge(phan_loai) {
  const map = {
    'Contract_Direct':    { bg: '#E6F1FB', fg: '#0C447C', label: 'DIRECT' },
    'Contract_Allocated': { bg: '#E1F5EE', fg: '#085041', label: 'ALLOCATED' },
    'Obtain_Contract':    { bg: '#FAEEDA', fg: '#633806', label: 'OBTAIN' },
    'Operating_SGA':      { bg: '#FBEAF0', fg: '#72243E', label: 'SGA' },
    'Inventory':          { bg: '#EEEDFE', fg: '#3C3489', label: 'INVENTORY' },
    'Prepaid':            { bg: '#FAECE7', fg: '#712B13', label: 'PREPAID' }
  };
  return map[phan_loai] || { bg: '#f0f0fa', fg: '#666', label: phan_loai || '?' };
}

// Hình thức TT label
function httLabel(v) {
  if (v === 'tien_mat' || v === 'tienmat') return 'Tiền mặt';
  if (v === 'chuyen_khoan' || v === 'chuyenkhoan') return 'Chuyển khoản';
  return v || '—';
}

// Static fake QR (placeholder, link to chat detail later when route exists)
function fakeQR() {
  return '<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">' +
    '<rect width="22" height="22" fill="#fff"/>' +
    '<rect x="0" y="0" width="6" height="6" fill="#000"/><rect x="1" y="1" width="4" height="4" fill="#fff"/><rect x="2" y="2" width="2" height="2" fill="#000"/>' +
    '<rect x="16" y="0" width="6" height="6" fill="#000"/><rect x="17" y="1" width="4" height="4" fill="#fff"/><rect x="18" y="2" width="2" height="2" fill="#000"/>' +
    '<rect x="0" y="16" width="6" height="6" fill="#000"/><rect x="1" y="17" width="4" height="4" fill="#fff"/><rect x="2" y="18" width="2" height="2" fill="#000"/>' +
    '<rect x="8" y="0" width="2" height="2" fill="#000"/><rect x="12" y="0" width="2" height="2" fill="#000"/>' +
    '<rect x="7" y="3" width="2" height="2" fill="#000"/><rect x="11" y="3" width="2" height="2" fill="#000"/><rect x="14" y="3" width="2" height="2" fill="#000"/>' +
    '<rect x="9" y="6" width="2" height="2" fill="#000"/><rect x="13" y="6" width="2" height="2" fill="#000"/>' +
    '<rect x="0" y="8" width="2" height="2" fill="#000"/><rect x="3" y="8" width="2" height="2" fill="#000"/><rect x="7" y="8" width="2" height="2" fill="#000"/><rect x="11" y="8" width="2" height="2" fill="#000"/><rect x="15" y="8" width="2" height="2" fill="#000"/><rect x="19" y="8" width="2" height="2" fill="#000"/>' +
    '<rect x="2" y="11" width="2" height="2" fill="#000"/><rect x="6" y="11" width="2" height="2" fill="#000"/><rect x="10" y="11" width="2" height="2" fill="#000"/><rect x="14" y="11" width="2" height="2" fill="#000"/><rect x="18" y="11" width="2" height="2" fill="#000"/>' +
    '<rect x="9" y="14" width="2" height="2" fill="#000"/><rect x="13" y="14" width="2" height="2" fill="#000"/><rect x="17" y="14" width="2" height="2" fill="#000"/>' +
    '<rect x="8" y="17" width="2" height="2" fill="#000"/><rect x="11" y="17" width="2" height="2" fill="#000"/><rect x="15" y="17" width="2" height="2" fill="#000"/>' +
    '<rect x="13" y="20" width="2" height="2" fill="#000"/><rect x="17" y="20" width="2" height="2" fill="#000"/>' +
    '</svg>';
}

// ── Main: Load all data needed + render HTML ─────────────────────────
export async function getPYCPrintHTML(pyctt_id) {
  // 1. Load PYCTT
  const { data: pyc, error: e1 } = await supabase.from('pyctt')
    .select('*').eq('id', pyctt_id).maybeSingle();
  if (e1 || !pyc) throw new Error('Không tải được PYC: ' + (e1?.message || 'không tìm thấy'));

  // 2. Load chi_phi items linked via ma_chi_tiet = so_pyctt
  const { data: items } = await supabase.from('chi_phi')
    .select('*').eq('ma_chi_tiet', pyc.so_pyctt).is('deleted_at', null).order('created_at');
  const cpItems = items || [];

  // 3. Load HĐ + dự án
  let hd = null, da = null;
  if (pyc.ma_hd) {
    const { data: hdRow } = await supabase.from('hop_dong')
      .select('id, so_hd, ma_hd, nha_thau, gia_tri, ma_du_an, du_an_id, phan_loai_hd')
      .or(`so_hd.eq.${pyc.ma_hd},ma_hd.eq.${pyc.ma_hd}`)
      .is('deleted_at', null).limit(1).maybeSingle();
    hd = hdRow || null;
  }

  // Resolve du_an: try du_an_id, then ma_du_an from hd, then pyc.ma_du_an
  if (hd?.du_an_id) {
    const { data: daRow } = await supabase.from('du_an')
      .select('ma_da, ten_da, chu_dau_tu').eq('id', hd.du_an_id).is('deleted_at', null).maybeSingle();
    da = daRow || null;
  }
  if (!da) {
    const maDa = hd?.ma_du_an || pyc.ma_du_an;
    if (maDa) {
      const { data: daRow } = await supabase.from('du_an')
        .select('ma_da, ten_da, chu_dau_tu').eq('ma_da', maDa).is('deleted_at', null).maybeSingle();
      da = daRow || null;
    }
  }

  // 3b. Resolve giá trị HĐ thật cho disbursement bar
  // Nếu là HDNT (gia_tri = 0), sum từ approved don_hang dưới HD này
  let giaTriHD = Number(hd?.gia_tri || 0);
  if (giaTriHD === 0 && hd?.id) {
    const { data: dhs } = await supabase.from('don_hang')
      .select('tong_gia_tri').eq('hop_dong_id', hd.id).eq('trang_thai', 'approved').is('deleted_at', null);
    giaTriHD = (dhs || []).reduce((s, d) => s + Number(d.tong_gia_tri || 0), 0);
  }

  // 4. Lũy kế trước: tổng chi_trong_ky của các PYC cùng ma_hd, đã approved, tạo trước phiếu này
  let luyKeTruoc = 0;
  if (pyc.ma_hd) {
    const { data: priors } = await supabase.from('pyctt')
      .select('chi_trong_ky, so_tien, created_at')
      .eq('ma_hd', pyc.ma_hd).eq('trang_thai', 'approved').is('deleted_at', null)
      .lt('created_at', pyc.created_at);
    luyKeTruoc = (priors || []).reduce((s, p) => s + Number(p.chi_trong_ky || p.so_tien || 0), 0);
  }

  // 5. Người duyệt L1/L2 (lookup user_profiles by UUID)
  const userIds = [pyc.approved_l1_by, pyc.approved_l2_by, pyc.created_by].filter(Boolean);
  let userMap = {};
  if (userIds.length) {
    const { data: users } = await supabase.from('user_profiles')
      .select('id, full_name, email').in('id', userIds);
    (users || []).forEach(u => { userMap[u.id] = u.full_name || u.email || ''; });
  }
  // We deliberately leave the names blank in the signature boxes per user request,
  // but still resolve them for the tiến trình if needed.

  // 6. Calc breakdown
  const tt = Number(pyc.thanh_tien || 0);
  const vat = Number(pyc.vat || pyc.vat_amount || 0);
  const sauVat = Number(pyc.sau_vat || (tt + vat));
  const giuQT = Number(pyc.giu_quyet_toan || 0);
  const giuBH = Number(pyc.giu_bao_hanh || 0);
  const truTU = Number(pyc.tru_tam_ung || 0);
  const truKhac = Number(pyc.tru_khac || 0);
  const chiTrongKy = Number(pyc.chi_trong_ky || (sauVat - giuQT - giuBH - truTU - truKhac));

  // 7. Disbursement tracker math
  const sauPhieu = luyKeTruoc + chiTrongKy;
  const conLai = Math.max(0, giaTriHD - sauPhieu);
  const pctTruoc = giaTriHD > 0 ? (luyKeTruoc / giaTriHD * 100) : 0;
  const pctNay = giaTriHD > 0 ? (chiTrongKy / giaTriHD * 100) : 0;
  const pctConLai = giaTriHD > 0 ? (conLai / giaTriHD * 100) : 0;
  const pctSauPhieu = giaTriHD > 0 ? (sauPhieu / giaTriHD * 100) : 0;

  // 8. Hash + watermark
  const hashSrc = `${pyc.so_pyctt}|${pyc.created_at}|${chiTrongKy}|${pyc.ma_hd || ''}`;
  const hash = await shortHash(hashSrc);
  const stateNorm = String(pyc.trang_thai || '').toLowerCase().trim();
  const wm = watermarkInfo(stateNorm);
  console.log('[printPYC] trang_thai=', pyc.trang_thai, '→ normalized=', stateNorm, '→ watermark=', wm);

  // 9. Build cost rows HTML
  let totalTT = 0;
  const rowsHTML = cpItems.map((ci, i) => {
    const ciTT = Number(ci.tong_tt || (Number(ci.so_luong || 0) * Number(ci.don_gia || 0)));
    totalTT += ciTT;
    const badge = ifrsBadge(ci.phan_loai);
    const vtSuffix = ci.ma_vat_tu ? ` <span style="font-family:monospace;font-size:7pt;color:#185FA5">(${E(ci.ma_vat_tu)})</span>` : '';
    return `<tr>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee;text-align:center">${i + 1}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee">${E(ci.ten_cp || ci.noi_dung || '')}${vtSuffix}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee;text-align:center">${E(ci.don_vi || '')}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee;text-align:right">${F(ci.so_luong)}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee;text-align:right">${F(ci.don_gia)}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee;text-align:right;font-weight:bold">${F(ciTT)}</td>
      <td style="padding:3px 4px;border-bottom:.5px solid #e2e4ee">${E(ci.loai_cp || '—')}<br><span style="display:inline-block;background:${badge.bg};color:${badge.fg};padding:0 4px;border-radius:2px;font-size:6pt;font-weight:bold;margin-top:1px">${badge.label}</span></td>
    </tr>`;
  }).join('');

  // 10. Header signature box content (user-requested: TGD = "VŨ VĂN LIÊM", others blank)
  const sig = {
    tgd: 'VŨ VĂN LIÊM',
    ktt: '',
    thuQuy: '',
    truongBP: '',
    ktThanhToan: '',
    nguoiDeNghi: ''
  };

  // 11. Watermark HTML
  const wmHTML = wm
    ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-20deg);font-size:96px;font-weight:900;color:${wm.color};opacity:0.22;letter-spacing:8px;pointer-events:none;white-space:nowrap;z-index:0;-webkit-print-color-adjust:exact;print-color-adjust:exact">${wm.text}</div>`
    : '';

  // 12. Render full HTML document
  const docTitle = `PYC ${pyc.so_pyctt}`;
  const exportTime = new Date().toLocaleString('vi-VN');
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${E(docTitle)}</title>
<style>
@page{size:A4 landscape;margin:12mm 15mm}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Cambria,Georgia,serif;font-size:9pt;color:#1a1a2e;background:#fff}
.page{width:267mm;min-height:186mm;margin:0 auto;display:flex;flex-direction:column;position:relative;overflow:hidden}
@media screen{body{background:#e8e8ee;padding:20px}.page{background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.15);padding:12mm 15mm;width:297mm}}
</style></head><body>
<div class="page">
${wmHTML}
<div style="position:relative;z-index:1;display:flex;flex-direction:column;flex:1">

<!-- HEADER -->
<div style="display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px solid #000066;padding-bottom:6px;margin-bottom:6px">
  <div style="display:flex;align-items:center;gap:8px">
    <div style="width:36px;height:36px;background:#000066;border-radius:6px;display:flex;align-items:center;justify-content:center;color:#FFCC00;font-weight:bold;font-size:13px">VN</div>
    <div><div style="font-size:12pt;font-weight:bold;color:#000066;letter-spacing:1px">VINAENC</div><div style="font-size:7pt;color:#888">Ban Kiểm Soát Nội Bộ</div></div>
  </div>
  <div style="text-align:center;flex:1">
    <div style="font-size:13pt;font-weight:bold;color:#000066;letter-spacing:1px">PHIẾU YÊU CẦU THANH TOÁN</div>
    ${da ? `<div style="font-size:8pt;color:#000066;margin-top:2px;font-weight:bold">Dự án: ${E(da.ten_da || da.ma_da)}${da.chu_dau_tu ? ` <span style="color:#888;font-weight:normal">— Chủ đầu tư: ${E(da.chu_dau_tu)}</span>` : ''}</div>` : ''}
  </div>
  <div style="text-align:right">
    <div style="font-size:7pt;color:#888">Số phiếu</div>
    <div style="font-size:10pt;font-weight:bold;color:#000066;font-family:monospace">${E(pyc.so_pyctt)}</div>
    <div style="font-size:7pt;color:#888;margin-top:2px">Ngày: ${fmtDateVN(pyc.created_at)}</div>
  </div>
</div>

<!-- META -->
<div style="display:grid;grid-template-columns:3fr 1fr;gap:10px;margin-bottom:4px">
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:5px">
    <div style="background:#f0f0fa;border-radius:3px;padding:3px 6px"><div style="font-size:6pt;color:#888;text-transform:uppercase;letter-spacing:.5px">Mã hợp đồng</div><div style="font-size:9pt;font-weight:bold">${E(pyc.ma_hd || '—')}</div></div>
    <div style="background:#f0f0fa;border-radius:3px;padding:3px 6px"><div style="font-size:6pt;color:#888;text-transform:uppercase;letter-spacing:.5px">Nhà thầu</div><div style="font-size:9pt;font-weight:bold">${E(hd?.nha_thau || pyc.nguoi_thu_huong || '—')}</div></div>
  </div>
  <div style="background:#f0f0fa;border-radius:3px;padding:3px 6px;border-left:1px solid #e2e4ee"><div style="font-size:6pt;color:#888;text-transform:uppercase;letter-spacing:.5px">Hình thức TT</div><div style="font-size:9pt;font-weight:bold">${E(httLabel(pyc.hinh_thuc_tt))}</div></div>
</div>

<!-- KỲ THANH TOÁN -->
<div style="background:#FFF8E1;border:1.5px solid #FFCC00;border-radius:4px;padding:5px 10px;margin-bottom:4px;display:flex;align-items:center;justify-content:space-between">
  <div style="font-size:7pt;color:#000066;text-transform:uppercase;letter-spacing:.5px;font-weight:bold">Kỳ thanh toán</div>
  <div style="font-size:9pt;font-weight:bold;color:#000066">${E(pyc.ky_tt || '—')}</div>
</div>

<!-- CONTENT GRID -->
<div style="display:grid;grid-template-columns:3fr 1fr;gap:10px">
  <!-- LEFT: Phần 1 -->
  <div>
    <div style="font-size:8pt;font-weight:bold;color:#000066;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px">Phần 1 — Chi tiết chi phí</div>
    <table style="width:100%;border-collapse:collapse;font-size:8pt">
      <thead><tr style="background:#000066;color:#FFCC00">
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:center;width:18px">#</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:left">Nội dung</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:center;width:30px">ĐVT</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:right;width:38px">KL</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:right;width:60px">Đơn giá</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:right;width:80px">Thành tiền</th>
        <th style="padding:3px 4px;font-size:6.5pt;font-weight:bold;text-align:left;width:90px">Loại CP / IFRS</th>
      </tr></thead>
      <tbody>${rowsHTML || '<tr><td colspan="7" style="padding:10px;text-align:center;color:#888">Không có chi phí chi tiết</td></tr>'}</tbody>
      <tfoot><tr>
        <td colspan="5" style="background:#f0f0fa;font-weight:bold;border-top:1px solid #000066;padding:3px 4px;text-align:right">Tổng cộng</td>
        <td style="background:#f0f0fa;font-weight:bold;border-top:1px solid #000066;padding:3px 4px;text-align:right;color:#000066">${F(totalTT)}</td>
        <td style="background:#f0f0fa;border-top:1px solid #000066;padding:3px 4px;text-align:center;font-size:6.5pt;color:#888">${cpItems.length} dòng</td>
      </tr></tfoot>
    </table>
  </div>

  <!-- RIGHT: Phần 2 -->
  <div style="border-left:1px solid #e2e4ee;padding-left:10px;display:flex;flex-direction:column">
    <div style="font-size:8pt;font-weight:bold;color:#000066;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px">Phần 2 — Thanh toán</div>
    <div style="background:#f8f9fc;border-radius:4px;padding:5px 7px;font-size:8pt">
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">Thành tiền</span><span style="font-weight:bold">${F(tt)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">VAT</span><span style="font-weight:bold">${F(vat)}</span></div>
      <div style="border-top:.5px solid #e2e4ee;margin:2px 0"></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666;font-weight:bold">Sau VAT</span><span style="font-weight:bold">${F(sauVat)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">Giữ QT 5%</span><span style="font-weight:bold;color:#c4000a">-${F(giuQT)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">Giữ BH 5%</span><span style="font-weight:bold;color:#c4000a">-${F(giuBH)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">Trừ TU</span><span style="font-weight:bold;color:#c4000a">-${F(truTU)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:1px 0"><span style="color:#666">Trừ khác</span><span style="font-weight:bold">${F(truKhac)}</span></div>
    </div>
    <div style="background:#FFF8E1;border:1.5px solid #FFCC00;border-radius:4px;padding:7px 4px;text-align:center;margin-top:5px">
      <div style="font-size:6pt;color:#000066;text-transform:uppercase;letter-spacing:1px;font-weight:bold">Chi trong kỳ</div>
      <div style="font-size:12pt;font-weight:bold;color:#000066;margin-top:2px;white-space:nowrap;font-family:monospace">${F(chiTrongKy)}</div>
    </div>
    <div style="font-size:6.5pt;color:#888;font-style:italic;text-align:center;margin-top:2px">${E(soThanhChu(chiTrongKy))}</div>
  </div>
</div>

<!-- THEO DÕI GIẢI NGÂN HỢP ĐỒNG -->
<div style="margin-top:6px;background:#f8f9fc;border:.5px solid #e2e4ee;border-left:3px solid #000066;border-radius:3px;padding:6px 10px">
  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px">
    <div style="font-size:7pt;font-weight:bold;color:#000066;text-transform:uppercase;letter-spacing:.5px">Theo dõi giải ngân Hợp đồng</div>
    <div style="font-size:7pt;color:#888">Tổng giá trị HĐ: <span style="color:#000066;font-weight:bold;font-family:monospace">${giaTriHD > 0 ? F(giaTriHD) + ' đ' : 'chưa cập nhật'}</span></div>
  </div>
  ${giaTriHD > 0 ? `
  <div style="display:flex;height:14px;border-radius:2px;overflow:hidden;border:.5px solid #d0d0d8">
    <div style="width:${pctTruoc.toFixed(1)}%;background:#000066;display:flex;align-items:center;justify-content:center;color:#fff;font-size:7pt;font-weight:bold">${pctTruoc >= 5 ? pctTruoc.toFixed(1) + '%' : ''}</div>
    <div style="width:${pctNay.toFixed(1)}%;background:#FFCC00;display:flex;align-items:center;justify-content:center;color:#000066;font-size:7pt;font-weight:bold">${pctNay >= 5 ? pctNay.toFixed(1) + '%' : ''}</div>
    <div style="width:${pctConLai.toFixed(1)}%;background:#e2e4ee;display:flex;align-items:center;justify-content:center;color:#666;font-size:7pt;font-weight:bold">${pctConLai >= 5 ? pctConLai.toFixed(1) + '%' : ''}</div>
  </div>
  ` : `
  <div style="height:14px;background:#f0f0fa;border:.5px dashed #c0c0d0;border-radius:2px;display:flex;align-items:center;justify-content:center;font-size:6.5pt;color:#888;font-style:italic">Chưa có giá trị HĐ — vui lòng cập nhật giá trị hợp đồng để hiển thị tiến độ giải ngân</div>
  `}
  <div style="display:flex;justify-content:space-between;font-size:7pt;margin-top:3px;flex-wrap:wrap;gap:6px">
    <div style="display:flex;align-items:center;gap:4px"><div style="width:8px;height:8px;background:#000066;border-radius:1px"></div><span style="color:#666">Lũy kế trước:</span> <span style="font-weight:bold;font-family:monospace">${F(luyKeTruoc)} đ</span></div>
    <div style="display:flex;align-items:center;gap:4px"><div style="width:8px;height:8px;background:#FFCC00;border-radius:1px"></div><span style="color:#666">Phiếu này:</span> <span style="font-weight:bold;font-family:monospace">${F(chiTrongKy)} đ</span></div>
    ${giaTriHD > 0 ? `<div style="display:flex;align-items:center;gap:4px"><div style="width:8px;height:8px;background:#e2e4ee;border:.5px solid #d0d0d8;border-radius:1px"></div><span style="color:#666">Còn lại HĐ:</span> <span style="font-weight:bold;font-family:monospace;color:#1B6B3A">${F(conLai)} đ</span></div>` : ''}
    <div><span style="color:#666">Đã chi sau phiếu này:</span> <span style="font-weight:bold;color:#000066;font-family:monospace">${F(sauPhieu)} đ${giaTriHD > 0 ? ' (' + pctSauPhieu.toFixed(1) + '%)' : ''}</span></div>
  </div>
</div>

<!-- CHỮ KÝ + FOOTER (sticky bottom) -->
<div style="margin-top:auto;padding-top:6px">
  <div style="display:grid;grid-template-columns:repeat(6,1fr);gap:4px">
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">TỔNG GIÁM ĐỐC</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.tgd)}</div></div>
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">KẾ TOÁN TRƯỞNG</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.ktt) || '&nbsp;'}</div></div>
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">THỦ QUỸ</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.thuQuy) || '&nbsp;'}</div></div>
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">TRƯỞNG BỘ PHẬN</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.truongBP) || '&nbsp;'}</div></div>
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">KT. THANH TOÁN</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.ktThanhToan) || '&nbsp;'}</div></div>
    <div style="border:.5px solid #e2e4ee;border-radius:3px;padding:3px;text-align:center"><div style="font-size:6pt;font-weight:bold;color:#000066;text-transform:uppercase">NGƯỜI ĐỀ NGHỊ</div><div style="height:76px"></div><div style="border-top:.5px solid #e2e4ee;padding-top:2px;font-size:7pt">${E(sig.nguoiDeNghi) || '&nbsp;'}</div></div>
  </div>
  <div style="display:flex;justify-content:space-between;align-items:center;font-size:6pt;color:#888;margin-top:4px;border-top:.5px solid #e2e4ee;padding-top:3px">
    <span>VNEC ERP v9.1 — Ban Kiểm Soát Nội Bộ — VINAENC</span>
    <span>Hash: <span style="font-family:monospace;color:#185FA5">${hash}</span></span>
    <span style="display:flex;align-items:center;gap:6px">${fakeQR()}<span>Verify QR</span></span>
    <span>Xuất: ${exportTime} — Trang 1/1</span>
  </div>
</div>

</div>
</div>
<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},300);});</script>
</body></html>`;

  return html;
}

// ── Public: Open print preview in new window ─────────────────────────
export async function printPYC(pyctt_id) {
  try {
    const html = await getPYCPrintHTML(pyctt_id);
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) {
      alert('Trình duyệt chặn popup. Vui lòng cho phép popup cho trang này.');
      return;
    }
    w.document.write(html);
    w.document.close();
  } catch (e) {
    alert('Lỗi xuất PDF: ' + e.message);
    console.error('[printPYC]', e);
  }
}
